import { useRef } from "react";
import {
  Clock, Monitor, Wallet, CheckCircle2, GraduationCap, Briefcase,
  Sparkles, Download, ArrowRight,
} from "lucide-react";
import { EnquiryForm } from "@/components/shared/EnquiryForm";
import { resolveMedia } from "@/lib/contentApi";

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur">
    <Icon className="h-4 w-4 text-orange" /> {children}
  </span>
);

const Section = ({ title, children, dark, id }) => (
  <section id={id} className={`py-14 md:py-20 ${dark ? "bg-navy text-white" : "bg-white text-navy"}`}>
    <div className="container-asai">
      {title && <h2 className="mb-8 font-heading text-3xl md:text-4xl">{title}</h2>}
      {children}
    </div>
  </section>
);

const BulletGrid = ({ items, icon: Icon = CheckCircle2 }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    {items.map((it, i) => (
      <div key={i} className="flex items-start gap-3 rounded-xl border border-navy/10 bg-lightgrey/50 p-4">
        <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange" />
        <span className="text-sm text-navy/80">{it}</span>
      </div>
    ))}
  </div>
);

const has = (v) => Array.isArray(v) ? v.length > 0 : Boolean(v && String(v).trim());

export default function CourseLandingView({ course }) {
  const enquiryRef = useRef(null);
  const c = course || {};
  const primaryText = c.primaryButtonText || "Enroll Now";
  const secondaryText = c.secondaryButtonText || "Book Free Demo";

  const scrollToEnquiry = () => enquiryRef.current?.scrollIntoView({ behavior: "smooth" });

  const PrimaryBtn = ({ full }) =>
    c.purchaseUrl ? (
      <a href={c.purchaseUrl} target="_blank" rel="noopener noreferrer" data-testid="course-enroll-btn"
         className={`inline-flex items-center justify-center gap-2 rounded-full bg-orange px-7 py-3 font-semibold text-white transition-transform duration-300 hover:scale-105 ${full ? "w-full sm:w-auto" : ""}`}>
        {primaryText} <ArrowRight className="h-4 w-4" />
      </a>
    ) : null;

  const SecondaryBtn = () =>
    c.demoType === "url" && c.demoUrl ? (
      <a href={c.demoUrl} target="_blank" rel="noopener noreferrer" data-testid="course-demo-btn"
         className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-3 font-semibold text-white transition-colors hover:bg-white/10">
        {secondaryText}
      </a>
    ) : (
      <button onClick={scrollToEnquiry} data-testid="course-demo-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-3 font-semibold text-white transition-colors hover:bg-white/10">
        {secondaryText}
      </button>
    );

  return (
    <div data-testid="course-landing">
      {/* HERO */}
      <header className="relative overflow-hidden bg-navy pt-28 pb-16 text-white md:pt-36 md:pb-24">
        {c.banner && (
          <div className="absolute inset-0">
            <img src={resolveMedia(c.banner)} alt="" className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/50" />
          </div>
        )}
        <div className="container-asai relative">
          {c.category && <span className="mb-4 inline-block rounded-full bg-orange/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange">{c.category}</span>}
          <h1 className="max-w-3xl font-heading text-4xl leading-tight sm:text-5xl lg:text-6xl">{c.name}</h1>
          {has(c.shortDescription) && <p className="mt-4 max-w-2xl text-base text-white/80 md:text-lg">{c.shortDescription}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            {has(c.duration) && <Chip icon={Clock}>{c.duration}</Chip>}
            {has(c.mode) && <Chip icon={Monitor}>{c.mode}</Chip>}
            {has(c.fees) && <Chip icon={Wallet}>{c.fees}</Chip>}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryBtn />
            <SecondaryBtn />
            {has(c.downloadSyllabusUrl) && (
              <a href={c.downloadSyllabusUrl} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white/90 hover:bg-white/10">
                <Download className="h-4 w-4" /> Download Syllabus
              </a>
            )}
          </div>
        </div>
      </header>

      {has(c.overview) && <Section title="Course Overview"><p className="max-w-3xl whitespace-pre-line text-lg leading-relaxed text-navy/80">{c.overview}</p></Section>}
      {has(c.detailedDescription) && !has(c.overview) && <Section title="About this Course"><p className="max-w-3xl whitespace-pre-line text-lg leading-relaxed text-navy/80">{c.detailedDescription}</p></Section>}

      {has(c.whyLearn) && <Section title="Why Learn This Course?" dark><p className="max-w-3xl whitespace-pre-line text-lg leading-relaxed text-white/85">{c.whyLearn}</p></Section>}

      {has(c.skills) && (
        <Section title="Skills & Technologies Covered">
          <div className="flex flex-wrap gap-3">
            {c.skills.map((s, i) => (
              <span key={i} className="rounded-xl border border-navy/10 bg-lightgrey px-4 py-2 text-sm font-medium text-navy">{s}</span>
            ))}
          </div>
        </Section>
      )}

      {has(c.highlights) && <Section title="Course Highlights"><BulletGrid items={c.highlights} icon={Sparkles} /></Section>}

      {has(c.syllabus) && (
        <Section title="Curriculum" dark>
          <div className="space-y-4">
            {c.syllabus.map((m, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="font-heading text-xl text-white">{i + 1}. {m.title}</h3>
                {m.points?.length > 0 && (
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {m.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/75"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange" /> {p}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {has(c.projects) && <Section title="Practical Projects"><BulletGrid items={c.projects} icon={Briefcase} /></Section>}
      {has(c.outcomes) && <Section title="Learning Outcomes"><BulletGrid items={c.outcomes} icon={GraduationCap} /></Section>}
      {has(c.careers) && <Section title="Career Opportunities" dark>
        <div className="flex flex-wrap gap-3">{c.careers.map((s, i) => <span key={i} className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90">{s}</span>)}</div>
      </Section>}

      {(has(c.trainerName) || has(c.trainerBio)) && (
        <Section title="Meet Your Trainer">
          <div className="flex flex-col gap-6 rounded-2xl border border-navy/10 bg-lightgrey/50 p-6 sm:flex-row sm:items-center">
            {c.trainerImage && <img src={resolveMedia(c.trainerImage)} alt={c.trainerName} className="h-28 w-28 rounded-2xl object-cover" />}
            <div>
              {has(c.trainerName) && <h3 className="font-heading text-2xl text-navy">{c.trainerName}</h3>}
              {has(c.trainerTitle) && <p className="text-sm font-semibold text-orange">{c.trainerTitle}</p>}
              {has(c.trainerBio) && <p className="mt-2 max-w-2xl whitespace-pre-line text-sm text-navy/75">{c.trainerBio}</p>}
            </div>
          </div>
        </Section>
      )}

      {has(c.whyChoose) && <Section title="Why Choose ASAI"><BulletGrid items={c.whyChoose} icon={CheckCircle2} /></Section>}

      {has(c.faqs) && (
        <Section title="Frequently Asked Questions" dark>
          <div className="mx-auto max-w-3xl space-y-3">
            {c.faqs.map((f, i) => (
              <details key={i} className="group rounded-xl border border-white/10 bg-white/5 p-4">
                <summary className="cursor-pointer list-none font-semibold text-white">{f.q}</summary>
                <p className="mt-2 text-sm text-white/75">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>
      )}

      {/* ENQUIRY / DEMO */}
      <section ref={enquiryRef} id="enquiry" className="bg-lightgrey py-14 md:py-20">
        <div className="container-asai grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-3xl text-navy md:text-4xl">Book a Free Demo / Get Counselling</h2>
            <p className="mt-3 max-w-md text-navy/70">Have questions about <strong>{c.name}</strong>? Share your details and our counsellor will reach out.</p>
            <div className="mt-6"><PrimaryBtn full /></div>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <EnquiryForm source={`course:${c.slug || c.name}`} defaultCourse={c.name} testId="course-enquiry-form" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      {c.purchaseUrl && (
        <section className="bg-navy py-16 text-center text-white">
          <div className="container-asai">
            <h2 className="font-heading text-3xl md:text-4xl">Ready to start {c.name}?</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/75">Secure your seat and begin your journey with ASAI.</p>
            <div className="mt-8 flex justify-center"><PrimaryBtn /></div>
          </div>
        </section>
      )}
    </div>
  );
}
